<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader />
			</LayoutFlex>
		</template>

		<template #content>
			<!-- Laid out as end-now (Figma 7415:18138): the title 24 under the logo row; title, card and list 20
			     apart; the button in the page 24 under the list. -->
			<LayoutFlex padding="var(--size-24) var(--size-20)" gap="var(--size-24)">
				<LayoutFlex padding="0" gap="var(--size-20)" align-items="center">
					<Text font="--heading-h3" color="--text-dark-primary" text-align="center">
						Here’s what happens if you cancel right now
					</Text>

					<BreakdownCard :rows="rows">
						<template v-if="repriced && breakdown" #default>
							Your {{ money(breakdown.monthlyRate) }}/month rate is based on a {{ breakdown.termMonths }}-month
							commitment. If you end your plan early, the medication you’ve already received is priced at the regular
							{{ money(breakdown.rate) }}/month rate.
						</template>
					</BreakdownCard>

					<ConsequenceList :items="consequences" />
				</LayoutFlex>

				<Button full-width :disabled="!option || processing" @ds:click="onConfirm">
					<Text font="--text-16-18-semi">Cancel and get {{ money(option?.refund) }} refund</Text>
				</Button>
			</LayoutFlex>
		</template>
	</LayoutScreen>

	<!-- "Processing your payment…" takeover (Figma 6847:86588) — an overlay, not a route: the page stays
	     mounted while the cancel and its refund are in flight. -->
	<Modal :visible="processing" fullscreen animation="none">
		<VerifyingScreen
			title="Processing your payment..."
			hint="Please don't go back or refresh the page while we process your transaction"
			title-width="272px"
		/>
	</Modal>
	<!-- The cancel call failed (500 / no connection): nothing was cancelled. "Try again" re-runs it here. -->
	<Modal :visible="cancelFailed" fullscreen animation="none">
		<CancelErrorScreen :step="step.title" @retry="retryConfirm" />
	</Modal>
</template>

<script setup lang="ts">
// /cancel/commitment/refund — Figma `kova cancel flow6` 6847:86881: end a paid-in-full plan today and
// get back what hasn't shipped (`upfront_refund` → `cancellation_type: supply_end`; the engine picks
// the refund over the instalments by plan). The line items come from the option's `paid` /
// `shipped_months` / `exit_rate`, the amount from its `refund`, the access line from `access_until`.
// The receipt is snapshotted before the call: the option is gone once the cancel lands.
//
// A 409 is "cancellation at supply end is not offered" — and our own earlier request (a double
// submit, a retry after a timeout) answers the same. So: re-read; `cancellation_type: supply_end`
// means it went through → the receipt; anything else → the options as they are now (the route guard
// sends a plan no longer offered the refund on to /status).
//
// Copy deviations from the frame (owner to confirm): "Your next 3-month supply won’t ship" — the frame
// reads "{12}-month supply", the plan length, but a supply is 3 months; "If you restart later" — the
// frame drops "you".
//
// Analytics (AL-15098): CANCEL_RIGHT_NOW_SUMMARY with `data.commitment` (`refund_usd`), closed with
// `cancel_and_refund` on the tap, then PROCESSING_PAYMENT around the call; a refund is `hard`.
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Button, LayoutFlex, LayoutScreen, Modal, Text } from '@simple-life-app/growth-ui-kova'
import CancelErrorScreen from '@/components/cancel/CancelErrorScreen.vue'
import CancelHeader from '@/components/CancelHeader.vue'
import BreakdownCard, { type BreakdownRow } from '@/components/cancel/BreakdownCard.vue'
import ConsequenceList from '@/components/cancel/ConsequenceList.vue'
import VerifyingScreen from '@/components/payment/VerifyingScreen.vue'
import { CancellationNotOfferedError } from '@/api/medBilling'
import { COMMITMENT_ROUTES, refundBreakdown, termMonths } from '@/cancelFlow/commitment'
import { formatDate, formatMoney, pluralize } from '@/cancelFlow/format'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useSubscriptionStore } from '@/store/subscription'
import { useSurveyStore } from '@/store/survey'
import {
	STEP_TITLE,
	commitmentStepData,
	processingPaymentStep,
	stateChangeOnCancel,
	useCancelStepLogging,
} from '@/analytics'

const router = useRouter()
const flow = useCancelFlowStore()
const subs = useSubscriptionStore()
const survey = useSurveyStore()

const q = computed(() => flow.quotes)
const option = computed(() => q.value.refund)
const processing = ref(false)
const cancelFailed = ref(false)

function money(amount: number | null | undefined) {
	return formatMoney(amount, q.value.currency)
}

const breakdown = computed(() => refundBreakdown(option.value, termMonths(subs.subscription)))

const step = useCancelStepLogging(STEP_TITLE.CANCEL_RIGHT_NOW_SUMMARY, {
	getCommitment: () =>
		commitmentStepData(subs.subscription, flow.kind, option.value, { fee: 0, refund: option.value?.refund ?? 0 }),
})

/** The plan rate was a discount on the exit rate — a renewal pays the regular rate, nothing to explain. */
const repriced = computed(() => !!breakdown.value && breakdown.value.monthlyRate < breakdown.value.rate)

const rows = computed<BreakdownRow[]>(() => {
	const total: BreakdownRow = { label: 'Your refund', value: money(option.value?.refund), emphasis: true }
	const b = breakdown.value
	if (!b) return [total]
	return [
		{ label: `${pluralize(b.termMonths, 'month')} already paid`, value: money(b.paid) },
		{ label: `${pluralize(b.shippedMonths, 'month')} received × ${money(b.rate)}`, value: `−${money(b.received)}` },
		total,
	]
})

const consequences = computed(() => {
	const until = option.value?.access_until
	return [
		'Your next 3-month supply won’t ship',
		'If you restart later, you’ll need a new intake and clinician approval before anything ships',
		...(until ? [`Your provider and the Kova app stay available until ${formatDate(until, 'short')}`] : []),
	]
})

async function onConfirm() {
	const o = option.value
	if (!o || processing.value) return
	processing.value = true
	const changed = stateChangeOnCancel('hard')
	step.done(['cancel_and_refund'])
	const closeProcessing = processingPaymentStep()
	const outcome = {
		kind: 'refunded' as const,
		refund: o.refund ?? 0,
		currency: q.value.currency,
		accessUntil: o.access_until ?? null,
	}
	try {
		await subs.cancel({ cancellationType: 'supply_end' })
		closeProcessing()
	} catch (err) {
		closeProcessing()
		if (!(err instanceof CancellationNotOfferedError)) {
			processing.value = false
			cancelFailed.value = true
			return
		}
		await subs.refreshSubscription()
		processing.value = false
		if (subs.subscription?.cancellation_type !== 'supply_end') {
			router.replace(COMMITMENT_ROUTES.options)
			return
		}
	}
	processing.value = false
	changed()
	flow.setOutcome({ ...outcome, accessUntil: subs.subscription?.expired_at ?? outcome.accessUntil })
	survey.reset()
	router.push('/cancel/done')
}

function retryConfirm() {
	cancelFailed.value = false
	onConfirm()
}
</script>
